import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

//Executive Pay
//TraderverseMarketExecutivePay
export class TraderverseMarketExecutivePay {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: { chart_data: any[]; summary: any } | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }



    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
      headerPinned: true,
            type: "Cash and Debt",
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            period: 'Yearly',
            topN: 20
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.attachStyles();
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Executive Pay</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="chartDv" id="executivePay">
                <div id="chartContainer" class="chart coverDv"></div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    }


    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            const topN = this.config.topN ?? 20;
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/executive-pay`,
                body: { topN },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            const chartData = Array.isArray(json?.data?.chart_data) ? json.data.chart_data : [];
            const summary = json?.data?.summary ?? null;
            this.currentData = { chart_data: chartData, summary };
            
            if (!chartData.length) {
                widgetBox?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
            widgetBox?.removeAttribute('data-testing-id');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.renderChartByType(this.config.type!);
    }

    private renderChartByType(type: string): void {
        const container = this.shadowRoot.querySelector('#executivePay');
        if (!container) return;

        // No HTML manipulation needed, just draw the chart
        this.drawChartByType();
    }

    private drawChartByType(): void {
        const wrapper = this.shadowRoot.querySelector('#chartContainer') as HTMLElement;
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
            const containerWrapper = this.shadowRoot.querySelector('#executivePay') as HTMLElement;

        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const chartData = [...(this.currentData?.chart_data ?? [])].reverse();
        if (chartData.length > 0) {
        containerWrapper?.setAttribute('data-testing-id', 'executive-pay');
    } else {
        containerWrapper?.removeAttribute('data-testing-id');
    }

        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const yAxisData = chartData.map((item: any) => item.name);
        const compensationValues = chartData.map((item: any) => item.pay / 1000000);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color');
        const axisLineColor = widgetStyles.getPropertyValue('--charts-axis-color');
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color');
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const fontSizeMd = widgetStyles.getPropertyValue('--font-size-md').trim() || '14px';
        const labelFontSize = parseInt(fontSize, 10) || 12;
        const accentColor = widgetStyles.getPropertyValue('--base-primary').trim() || '#144BFF';

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const item = params[0];
                    const index = item.dataIndex;
                    const executive = chartData[index];
                    
                    return `
                    <div style="font-size:${fontSize}; line-height:1.45; max-width:280px;">
                        <div style="font-size:${fontSizeMd}; font-weight:600; margin-bottom:4px;">${executive.name}</div>
                        <div>${executive.symbol}</div>
                        <div>${executive.title || ''}</div>
                        <hr style="margin:6px 0; border:0; border-top:1px solid ${axisLineColor};">
                        <div>Pay: <b>${executive.pay_display || `$${(executive.pay / 1000000).toFixed(1)}M`}</b></div>
                        ${executive.year_born ? `<div>Year Born: ${executive.year_born}</div>` : ''}
                        ${executive.gender ? `<div>Gender: ${executive.gender}</div>` : ''}
                        ${executive.currency_pay ? `<div>Currency: ${executive.currency_pay}</div>` : ''}
                    </div>
                `;
                }
            }, 
            grid: { left: 12, right: 90, top: 22, bottom: 34, containLabel: true },
            xAxis: {
                type: 'value',
                name: 'Executive Pay ($M)',
                nameLocation: 'middle',
                nameGap: 28,
                nameTextStyle: { 
                    color: textColor,
                    fontSize: labelFontSize
                },
                axisLabel: { 
                    color: textColor, 
                    fontSize,
                    margin: 12,
                    formatter: (value: number) => `$${value}M`
                },
                splitLine: { 
                    lineStyle: { 
                        color: splitLineColor,
                        type: 'dashed'
                    } 
                },
                axisLine: {
                    lineStyle: {
                        color: axisLineColor
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: yAxisData,
                axisLabel: {
                    color: textColor,
                    fontSize,
                    interval: 0,
                    margin: 14
                },
                axisTick: { 
                    show: false,
                    alignWithLabel: true
                },
                axisLine: {
                    lineStyle: {
                        color: axisLineColor
                    }
                },
                splitLine: { 
                    show: false 
                }
            },
            series: [
                {
                    name: 'Executive Pay ($M)',
                    type: 'bar',
                    data: compensationValues,
                    barWidth: '48%',
                    barCategoryGap: '34%',
                    itemStyle: {
                        borderRadius: [0, 4, 4, 0],
                        color: accentColor
                    },
                    label: {
                        show: true,
                        position: 'right',
                        distance: 8,
                        formatter: (params: any) => `$${Number(params.value).toFixed(1)}M`,
                        color: textColor,
                        fontSize
                    }
                }
            ],
            animation: true,
            animationDuration: 1000,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container);
        chart.setOption(option);
        
        // Handle window resize
        const resizeHandler = () => chart.resize();
        window.addEventListener('resize', resizeHandler);
        
        // Clean up resize listener when chart is disposed
        chart.on('disposed', () => {
            window.removeEventListener('resize', resizeHandler);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketExecutivePay: typeof TraderverseMarketExecutivePay; } }
if (typeof window !== 'undefined') { window.TraderverseMarketExecutivePay = TraderverseMarketExecutivePay; }